import { Injectable } from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { PDFDocument } from 'pdf-lib';
import * as Sentry from '@sentry/angular';
import {
  BooleanCons,
  EstatusTypeCard,
  LimitGlobal,
  LineProductCons,
  MessageType,
  NumberCons,
  PaymentTypeInfo,
  PaymentTypeInfoCompare,
  RolesString,
  RoutesCons,
  Status,
  StatusLegends,
  StringConst
} from '../constants/const';
import { Messages } from '../constants/messages';
import { noDiagnosis } from '../constants/recipe';
import { SpinnerNames } from '../constants/spinners';
import { NotificationMessageComponent } from '../dialogs/notification-message/notification-message.component';
import { TypeIcon } from '../model/device/message.model';
import { ItemPackagesSent } from '../model/http/activeProducts.model';
import {
  CatalogFields,
  ICatalogs,
  ItemClassification
} from '../model/http/catalogs';
import {
  BackIProductDetailResponse,
  BackIProductDetailStorage
} from '../model/http/historyOrders.model';
import { ItemHome } from '../model/http/home.model';
import {
  CallPlace,
  DetailProduct,
  IProducts,
  ProductAttributes,
  TypeShoppingCar
} from '../model/http/myActivity.model';
import { PatientsConfigByReRecipe, ProductItem, RecipeSelectedProduct } from '../model/http/prescriptionName';
import { DirectionsAndTaxs } from '../model/http/profile.model';
import { UserInfo } from '../model/http/security.model';
import { CartContentType, ItemValidateLabel, PrescritionInfoHeader, ProductMaxPieces, ProductMinPieces } from '../model/http/shoppingList';
import { ValidateSameValues } from '../model/local/details.orders';
import { BreadcrumbsItems } from '../model/local/menu-items.model';
import { PaymentInformation } from '../model/local/payment.model';
import { PrescriptionName } from '../model/local/prescription-name.model';
import { PrescriptionNamesView } from '../model/local/prescriptionNamesView';
import { SigInRols } from '../model/local/rols.model';
import { ReloadShoppingCar, UpdateProduct } from '../model/local/shopping-cart';
import { SucessPayOptions } from '../model/local/success-pay.model';
import { DialogService } from './dialog.service';
import { LocalStorageService } from './local-storage.service';
import { ObservableService } from './observable.service';
import { SAPFilterRequest } from '../model/local/sapfilters.model';
import { TypeReportsFilter, ValueTypesFilter } from '../constants/sap.filters.titles';
import * as moment from 'moment';
import { SelectedDateRange } from '../model/local/general.model';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private userInfo = new UserInfo();
  BREADCRUMBS_ACTIONS: { [key: string]: BreadcrumbsItems } = {};
  SUCCESS_PAY_OPTIONS: { [key: number]: SucessPayOptions } = {};
  PACKAGES_STATUS: { [key: string]: ItemPackagesSent } = {};
  private hasChanges = false;

  constructor(
    private localStorageService: LocalStorageService,
    private observableService: ObservableService,
    private dialogService: DialogService,
  ) { }

  buildDictionaries(): void {
    this.BREADCRUMBS_ACTIONS = {
      3: this.getBreadcrumbsForListShoppingCart(),
      4: this.getBreadcrumbsForDetailOrderList(),
      35: this.getBreadcrumbsForPendingOrder(),
    };

    this.SUCCESS_PAY_OPTIONS = {
      24: this.getOptionsForConfirmOrder(),
      35: this.getOptionsForPendingOrder(),
      31: this.getOptionsForIncidents(),
      32: this.getOptionsForInvoicesPending(),
      18: new SucessPayOptions(),
    };

    this.PACKAGES_STATUS = {
      'En Camino': this.getPackageStatusTheWay(),
      Entregado: this.getPackageStatusDelivered(),
      Enviado: this.getPackageStatusSent(),
      'No Entregado': this.getPackageStatusNotDelivered(),
    };
  }

  getLabelsByItemCode = (
    itemCatalogs: ICatalogs[],
    itemCode: string
  ): ICatalogs[] =>
    itemCatalogs.filter((paramSearch) =>
      this.validateOrValue(
        paramSearch.itemCode.includes('*'),
        paramSearch.itemCode.includes(itemCode)
      )
    )

  validateisIncludeLabelDefault(
    itemCatalogs: ICatalogs[],
    label: string,
    validate: boolean
  ): ICatalogs[] {
    if (
      this.validateAndValue([
        !this.filterByValue(itemCatalogs).includes(label),
        validate,
      ])
    ) {
      const itemLabel = new ICatalogs();
      itemLabel.description = label;
      itemLabel.value = label;
      itemCatalogs.push(itemLabel);
    }
    return itemCatalogs;
  }

  getIsContainsLabel(
    itemCatalogs: ICatalogs[],
    label: string,
    notLine: boolean
  ): boolean {
    const includeLabel = this.filterByValue(itemCatalogs).includes(label);
    return this.calculateTernary(!notLine, BooleanCons.true, includeLabel);
  }

  getValidateLabels(
    attributes: ProductAttributes[],
    labelList: ICatalogs[],
    notLine: boolean
  ): ProductAttributes[] {
    attributes.forEach((attribute) => {
      const isAdvisor = this.localStorageService.getUserInRole() === RolesString.advisor;
      attribute.originLabel = attribute.label;
      const includeLabel = this.filterByValue(labelList).includes(
        attribute.label
      );
      const labelSelected = this.calculateTernary(
        includeLabel,
        attribute.label,
        StringConst.empty
      );
      const label = this.calculateTernary(
        notLine,
        labelSelected,
        attribute.label
      );
      attribute.label = this.calculateTernary(!isAdvisor, label, attribute.originLabel);
    });
    return attributes;
  }

  filterByValue = (itemCatalogs: ICatalogs[]): string[] =>
    itemCatalogs.map((filterValue) => filterValue.value)

  getInitialsName(name: string): string {
    let nametemp: string[];
    let lastName: string[];
    let nameReturn = StringConst.empty;
    const nameString = name
      .split(' ')
      .filter((filterName) => filterName !== StringConst.empty);
    if (nameString.length > 1) {
      nametemp = nameString[0].split(StringConst.empty);
      lastName = nameString[1].split(StringConst.empty);
      nameReturn = nametemp[0] + lastName[0];
    }
    return nameReturn;
  }

  getLabelListError(products: IProducts[]): ItemValidateLabel[] {
    const list: ItemValidateLabel[] = [];
    products.forEach((product) => {
      const filterList: ItemValidateLabel[] = product.productAttributes
        .filter((attribute) => attribute.label === StringConst.empty)
        .map((item) => {
          return {
            container: item.container,
            itemCode: product.itemCode,
            label: item.originLabel,
            description: product.description
          };
        });
      list.push(...filterList);
    });
    return list;
  }

  getProductToUpdateObj(
    product: IProducts,
    currentPieces: number,
    container: string,
    label: string,
    containerToReplace: string,
    needSumQuantity: boolean,
    needCalculatePrices: boolean,
    typeCart: number,
    priceListId: number
  ): UpdateProduct {
    const productToUpdate = new UpdateProduct();
    productToUpdate.cartType = typeCart;
    productToUpdate.container = this.calculateTernary(
      product.isLine === LineProductCons.yes,
      LineProductCons.noApply,
      container
    );
    productToUpdate.itemCode = product.itemCode;
    productToUpdate.label = this.calculateTernary(
      product.isLine === LineProductCons.yes,
      LineProductCons.noApply,
      label
    );
    productToUpdate.needSumQuantity = needSumQuantity;
    productToUpdate.needsPersitance = needCalculatePrices;
    productToUpdate.quantity = currentPieces;
    productToUpdate.userId = this.localStorageService.getCardCodeGeneral();
    productToUpdate.isLine = product.isLine ?? LineProductCons.no;
    productToUpdate.needsFavorite = true;
    productToUpdate.containerToReplace = containerToReplace;
    productToUpdate.priceListId = priceListId;
    return productToUpdate;
  }
  goToSpecificElementRef(documentRef: Document, idElementRef: string): void {
    const elementRef = documentRef.querySelector(`#${idElementRef}`) || {
      scrollIntoView: () => { },
    };
    elementRef.scrollIntoView();
  }

  getTypeImage(typeResult: string): string {
    return typeResult.split('/')[1];
  }

  getOnlyBase64 = (fullBase64: any): string => fullBase64.split(',')[1];

  getTotalTimesToCall(comments: string): number {
    return Math.ceil(this.getGrantTotalOnService(comments) / LimitGlobal);
  }

  getGrantTotalOnService = (grantTotal: any): number =>
    Number(grantTotal.split('-')[NumberCons.zero])

  updateDetailOrderDataEditStorage(
    init: number,
    initTaxData: number,
    initPayment: number,
    initDirection: number,
    object: BackIProductDetailResponse,
    transactionId: string
  ): BackIProductDetailStorage {
    const getObjectBack = new BackIProductDetailStorage();
    getObjectBack.init = init;
    getObjectBack.initTaxData = initTaxData;
    getObjectBack.initPaymentData = initPayment;
    getObjectBack.initDirection = initDirection;
    getObjectBack.object = object;
    getObjectBack.cardCode = this.localStorageService.getCardCodeGeneral();
    getObjectBack.transactionId = transactionId;
    return getObjectBack;
  }
  updateObjectLocalStorage(
    backIProductDetailStorage: BackIProductDetailStorage,
    objectBack: BackIProductDetailResponse,
    keyStorage: string
  ): void {
    const submit = this.updateDetailOrderDataEditStorage(
      backIProductDetailStorage.init,
      backIProductDetailStorage.initTaxData,
      backIProductDetailStorage.initPaymentData,
      backIProductDetailStorage.initDirection,
      objectBack,
      backIProductDetailStorage.transactionId
    );
    this.localStorageService.setDetailOrderDataEdit(submit, keyStorage);
  }

  getIconOfPaymentMethod(method: string): string {
    return `./assets/icons/payment_methods/${method}.svg`;
  }

  convertStringToBase64(imageStr: string, type: string): string {
    return `data:image/${type};base64,${imageStr}`;
  }

  getParamsBreadCrumbs(place: CallPlace): BreadcrumbsItems {
    return this.BREADCRUMBS_ACTIONS[place] ?? new BreadcrumbsItems();
  }
  getBreadcrumbsItemsBackOrderResume(callPlace: CallPlace): string {
    const routesDict: { [key: number]: string } = {
      [CallPlace.pendingOrders]: RoutesCons.pendingOrder,
      [CallPlace.detailOrderList]: RoutesCons.taxData,
      [CallPlace.listShoppingCart]: RoutesCons.taxData,
    };
    return routesDict[callPlace] ?? StringConst.empty;
  }

  private getBreadcrumbsForListShoppingCart(): BreadcrumbsItems {
    const breadcrumbsItems = new BreadcrumbsItems();
    breadcrumbsItems.historyOrder = false;
    breadcrumbsItems.shoppingCar = true;
    breadcrumbsItems.editarAndBuy = false;
    breadcrumbsItems.deliveryData = true;
    breadcrumbsItems.taxData = true;
    return breadcrumbsItems;
  }

  private getBreadcrumbsForDetailOrderList(): BreadcrumbsItems {
    const rol = this.localStorageService.getUserInRole();
    const breadcrumbsItems = new BreadcrumbsItems();
    breadcrumbsItems.historyOrder = rol === RolesString.doctor;
    breadcrumbsItems.generateOrder = rol !== RolesString.doctor;
    breadcrumbsItems.shoppingCar = false;
    breadcrumbsItems.editarAndBuy = true;
    breadcrumbsItems.deliveryData = true;
    breadcrumbsItems.taxData = true;
    return breadcrumbsItems;
  }
  private getBreadcrumbsForPendingOrder(): BreadcrumbsItems {
    const breadcrumbsItems = new BreadcrumbsItems();
    return breadcrumbsItems;
  }

  getUserInfo(): UserInfo {
    return this.userInfo;
  }

  setUserInfo(userInfo: UserInfo): void {
    this.userInfo = userInfo;
  }

  openNewTapByUrl(url: string, windowInstance?: Window): void {
    windowInstance = windowInstance || window;
    windowInstance.open(url, '_blank');
  }


  getCardTypeForRolShoppingCard(rol: string): number {
    return this.calculateTernary(
      rol === RolesString.doctor,
      EstatusTypeCard.doctorShopping,
      EstatusTypeCard.advisorShopping
    );
  }
  getSmallDimensions(): boolean {
    const rol = this.localStorageService.getUserInRole();
    return this.calculateTernary(rol === RolesString.advisor, true, false);
  }
  getCardTypeForRol(rol: string, callPlace: CallPlace): number {
    const cardTypeForRole = [
      {
        rol: RolesString.doctor,
        callPlace: CallPlace.listShoppingCart,
        statusType: EstatusTypeCard.doctorShopping,
      },
      {
        rol: RolesString.doctor,
        callPlace: CallPlace.detailOrderList,
        statusType: EstatusTypeCard.doctorEdit,
      },
      {
        rol: RolesString.advisor,
        callPlace: CallPlace.listShoppingCart,
        statusType: EstatusTypeCard.advisorShopping,
      },
      {
        rol: RolesString.advisor,
        callPlace: CallPlace.detailOrderList,
        statusType: EstatusTypeCard.advisorEdit,
      },
      {
        rol: RolesString.doctor,
        callPlace: CallPlace.pendingOrders,
        statusType: EstatusTypeCard.withoutTypeCard,
      },
    ];
    return (
      cardTypeForRole.find((type) =>
        this.validateAndValue([type.rol === rol, type.callPlace === callPlace])
      )?.statusType ?? 1
    );
  }
  getImgStatus(status: string): string {
    return `./assets/icons/${status}.svg`;
  }

  getDateViewDetailTimeLinea(packages: ItemPackagesSent): ItemPackagesSent {
    const result = this.PACKAGES_STATUS[packages.status];
    packages.dateView = result.dateView;
    packages.isViewDeliveryCompany = result.isViewDeliveryCompany;
    packages.viewStatus = result.viewStatus;
    packages.legendsStatus = this.calculateTernary(
      packages.status === Status.delivered,
      `${result.legendsStatus}${packages.deliveredTo}`,
      `${result.legendsStatus}`
    );
    packages.listDeliveryAddres = packages.deliveryAddress.split('# ');
    return packages;
  }

  private getPackageStatusTheWay(): ItemPackagesSent {
    const packages = new ItemPackagesSent();
    packages.dateView = packages.inWayDate;
    packages.legendsStatus = StatusLegends.theWay;
    packages.isViewDeliveryCompany = false;
    packages.viewStatus = Status.route;
    return packages;
  }

  private getPackageStatusDelivered(): ItemPackagesSent {
    const packages = new ItemPackagesSent();
    packages.dateView = packages.deliveredDate;
    packages.legendsStatus =
      StatusLegends.delivered + ' ' + packages.deliveredTo;
    packages.isViewDeliveryCompany = true;
    packages.viewStatus = Status.delivered;
    return packages;
  }
  private getPackageStatusSent(): ItemPackagesSent {
    const packages = new ItemPackagesSent();
    packages.dateView = packages.deliveredDate;
    packages.isViewDeliveryCompany = true;
    packages.viewStatus = Status.sentView;
    return packages;
  }
  private getPackageStatusNotDelivered(): ItemPackagesSent {
    const packages = new ItemPackagesSent();
    packages.dateView = packages.deliveredDate;
    packages.isViewDeliveryCompany = false;
    packages.viewStatus = Status.notDelivered;
    return packages;
  }

  getInputAppareance(isAdvisor: boolean): MatFormFieldAppearance {
    return this.calculateTernary(isAdvisor, 'legacy', 'outline');
  }

  getSuccessPayOptions(callPlace: CallPlace): SucessPayOptions {
    return this.SUCCESS_PAY_OPTIONS[callPlace];
  }

  getOptionsForConfirmOrder(): SucessPayOptions {
    const successPayOptions = new SucessPayOptions();
    successPayOptions.text.title = BooleanCons.true;
    successPayOptions.text.subtitle = BooleanCons.true;
    successPayOptions.text.icon = BooleanCons.true;
    successPayOptions.text.processingPayText = BooleanCons.true;
    successPayOptions.text.discountText = BooleanCons.true;
    successPayOptions.text.titleTextTransferSpei = BooleanCons.true;
    successPayOptions.text.titleSpei = BooleanCons.true;
    successPayOptions.text.subtitleSpei = BooleanCons.true;
    successPayOptions.text.iconSpei = BooleanCons.true;
    successPayOptions.text.titlePaynet = BooleanCons.true;
    successPayOptions.text.subtitlePaynet = BooleanCons.true;
    successPayOptions.text.iconPaynet = BooleanCons.true;
    successPayOptions.buttons.goToOrders = BooleanCons.true;
    successPayOptions.buttons.goToHome = BooleanCons.true;
    successPayOptions.buttons.goToPendingOrdersAdvisor = BooleanCons.true;
    successPayOptions.buttons.goToHomeAdvisor = BooleanCons.true;
    successPayOptions.buttons.centerButton = BooleanCons.false;
    successPayOptions.text.pricesChange = BooleanCons.true;
    return successPayOptions;
  }

  getOptionsForPendingOrder(): SucessPayOptions {
    const successPayOptions = new SucessPayOptions();
    successPayOptions.text.title = BooleanCons.true;
    successPayOptions.text.subtitle = BooleanCons.true;
    successPayOptions.text.icon = BooleanCons.true;
    successPayOptions.text.processingPayText = BooleanCons.true;
    successPayOptions.text.discountText = BooleanCons.true;
    successPayOptions.text.titleTextTransferSpei = BooleanCons.true;
    successPayOptions.text.reverseInfo = BooleanCons.true;
    successPayOptions.buttons.accept = BooleanCons.true;
    successPayOptions.text.pricesChange = BooleanCons.true;
    return successPayOptions;
  }

  getOptionsForIncidents(): SucessPayOptions {
    const successPayOptions = new SucessPayOptions();
    successPayOptions.text.title = BooleanCons.true;
    successPayOptions.text.subtitle = BooleanCons.true;
    successPayOptions.text.icon = BooleanCons.true;
    successPayOptions.buttons.accept = BooleanCons.true;
    return successPayOptions;
  }

  getOptionsForInvoicesPending(): SucessPayOptions {
    const successPayOptions = new SucessPayOptions();
    successPayOptions.text.title = BooleanCons.true;
    successPayOptions.text.subtitle = BooleanCons.true;
    successPayOptions.text.icon = BooleanCons.true;
    successPayOptions.buttons.accept = BooleanCons.true;
    successPayOptions.text.titlePaynet = BooleanCons.false;
    successPayOptions.text.titleSpei = BooleanCons.false;
    successPayOptions.text.titleTextTransferSpei = BooleanCons.true;
    return successPayOptions;
  }

  transformToPrescriptionNameOrUpdate(
    recipes: PatientsConfigByReRecipe[],
    calledFromConfirm: boolean
  ): PrescriptionNamesView[] | PrescriptionName[] {
    const prescriptionsResult: PrescriptionNamesView[] = [];
    recipes.forEach((recipeItem, recipeIndex) => {
      recipeItem.patients.forEach((patient) => {
        const itemResult = this.calculateTernary(
          calledFromConfirm,
          new PrescriptionName(),
          new PrescriptionNamesView()
        );

        if (calledFromConfirm) {
          itemResult.num = recipeIndex;
        } else {
          itemResult.numPrescription = recipeIndex;
        }
        itemResult.diagnosis =
          patient.diagnosis?.diagnosis ?? StringConst.empty;
        itemResult.diagnosis = itemResult.diagnosis.replace(
          noDiagnosis.diagnosis,
          ''
        );
        itemResult.gender = patient.gender;
        itemResult.isNamePrinted = Number(patient.isNamePrinted);
        itemResult.name = patient.patientName;
        itemResult.dosage = patient.dosage;
        if (patient.birthDate) {
          if (patient.birthDate instanceof Date) {
            itemResult.birthDate = patient.birthDate.toISOString();
          } else if (typeof patient.birthDate === 'string') {
            itemResult.birthDate = patient.birthDate;
          } else {
            itemResult.birthDate = this.getDefaultBirthDate().toISOString();
          }
        } else {
          itemResult.birthDate = this.getDefaultBirthDate().toISOString();
        }
        itemResult.products = patient.piecesAndProducts.map((product) => {
          return {
            itemCode: product.product,
            quantity: product.pieces,
            description: StringConst.empty,
            label: product.label,
            container: product.container
          };
        });
        prescriptionsResult.push(itemResult);
      });
    });
    return prescriptionsResult;
  }

  getDefaultBirthDate(): Date {
    const minAge = 19;
    const maxAge = 90;
    const randomAge = Math.floor(Math.random() * (maxAge - minAge + 1)) + minAge;
    const date = new Date();
    date.setFullYear(date.getFullYear() - randomAge);
    const randomMonth = Math.floor(Math.random() * 12);
    date.setMonth(randomMonth);
    const randomDay = Math.floor(Math.random() * 28) + 1;
    date.setDate(randomDay);
    return date;
  }

  async createPDF(urls: string[], windowInstance?: Window): Promise<void> {
    windowInstance = windowInstance || window;
    const urlsValid = urls.filter((url) =>
      this.validateAndValue([url !== StringConst.empty, url.endsWith('.pdf')])
    );
    if (urlsValid.length > 0) {
      this.observableService.setIsLoading({
        name: SpinnerNames.principal,
        status: true,
      });
      try {
        const pdfDoc = await this.getPDFByUrl(urls, windowInstance);
        const pdfBytes = await pdfDoc.save();
        const file = new Blob([pdfBytes], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        this.observableService.setIsLoading({
          name: SpinnerNames.principal,
          status: false,
        });
        windowInstance.open(fileURL, '_blank');
      } catch (error) {
        if (!error?.reportedToSentry) {
          Sentry.captureException(error, {
            tags: { feature: 'create-pdf', stage: 'parse' },
            extra: { urls },
          });
        }
      }
      return;
    }
    this.errorPDFMessage(Messages.pdfEmpty);
  }

  async getPDFByUrl(urls: string[], windowInstance: Window): Promise<PDFDocument> {
    const pdfDoc = await PDFDocument.create();
    for (const url of urls) {
      const flagPdfBytes = await windowInstance.fetch(url).then((res) => {
        if (res.ok) {
          return res.arrayBuffer();
        }
        return Promise.reject(res.statusText);
      }).catch((error) => {
        this.observableService.setIsLoading({
          name: SpinnerNames.principal,
          status: false,
        });
        this.errorPDFMessage(Messages.pdfUploadFailed);
        const reason = error instanceof Error ? 'network-error' : 'http-error';
        const wrappedError = new Error(`createPDF: failed to fetch PDF (${reason}): ${error?.message ?? error}`);
        Sentry.captureException(wrappedError, {
          tags: { feature: 'create-pdf', stage: 'fetch', reason },
          extra: { url, originalError: String(error) },
          fingerprint: ['create-pdf-fetch-error', reason],
        });
        throw Object.assign(new Error(error), { reportedToSentry: true });
      });

      const pdfLoaded = await PDFDocument.load(flagPdfBytes);
      const pdfPages = pdfLoaded.getPages();
      for (const page of pdfPages) {
        const pdfEmbed = await pdfDoc.embedPage(page);
        const newPdfPage = pdfDoc.addPage();
        newPdfPage.setWidth(page.getWidth());
        newPdfPage.setHeight(page.getHeight());
        newPdfPage.drawPage(pdfEmbed);
      }
    }
    return Promise.resolve(pdfDoc);
  }

  errorPDFMessage(message: string): void {
    this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
      typeIcon: TypeIcon.error,
      textToShow: message,
      isButtonCancel: false,
      typeMessage: MessageType.none,
    });
  }

  validateRegex(regex: RegExp, value: string): boolean {
    const regexExp = new RegExp(regex);
    return regexExp.test(value);
  }

  validateRolForRouterRestart(): string {
    return this.calculateTernary(
      this.localStorageService.getUserInRole() === RolesString.doctor,
      RoutesCons.shoppingCar,
      RoutesCons.doctorList
    );
  }
  validateRolForRouterRestartByGenerateOrder(status: number): string[] {
    const getTaxDataStringsDict: { [key: number]: string[] } = {
      [EstatusTypeCard.advisorShopping]: [RoutesCons.shoppingCar],
      [EstatusTypeCard.doctorEdit]: [RoutesCons.historyOrders],
      [EstatusTypeCard.doctorShopping]: [RoutesCons.shoppingCar],
      [EstatusTypeCard.advisorEdit]: [RoutesCons.generateOrderAdvisor,
      this.localStorageService.getCardCodeGeneral()],
      [EstatusTypeCard.withoutTypeCard]: [RoutesCons.pendingOrder],
    };
    return getTaxDataStringsDict[status] ?? [];
  }

  validateRolForRouteGenerateOrder(cardCode: string): string[] {
    return this.calculateTernary(
      this.localStorageService.getUserInRole() === RolesString.doctor,
      [RoutesCons.generateOrder],
      [RoutesCons.generateOrderAdvisor, cardCode]
    );
  }

  getValidateCardCodeGeneral = (backCardCode: string): boolean =>
    this.validateAndValue([
      this.localStorageService.getCardCodeGeneral() !== StringConst.empty,
      this.localStorageService.getCardCodeGeneral() === backCardCode,
    ])

  validateSameValues = (valuesSame: ValidateSameValues[]): boolean =>
    valuesSame.every((value) => value.firstValue === value.secondValue)

  validateSomeValues = (valuesSome: ValidateSameValues[]): boolean =>
    valuesSome.some((value) => value.firstValue === value.secondValue)

  validateValidString = (value: string | undefined): boolean =>
    this.validateAndValue([value !== '', value !== null, value !== undefined])

  validateOrValue = (value1: boolean, value2: boolean): boolean =>
    value1 || value2

  getCarCodeFromDoctorOrAdvisor(value: boolean): string {
    return this.calculateTernary(
      value,
      this.localStorageService.getCardCode(),
      this.localStorageService.getCardCodeDoctorFromAdvisorRole()
    );
  }

  validateActiveRols(): SigInRols {
    let rols = new SigInRols();
    const rol = this.localStorageService.getUserInRole();
    const dictRols: { [key: string]: SigInRols } = {
      doctor: { isDoctor: true, isAdvisor: false, isReporter: false },
      advisor: { isDoctor: false, isAdvisor: true, isReporter: false },
      reporter: { isDoctor: false, isAdvisor: false, isReporter: true },
    };
    rols = dictRols[rol] ?? new SigInRols();
    return rols;
  }

  validateAndValue = (list: boolean[]): boolean =>
    list.every((value) => value === true)

  xorTwoValues(firstValue: boolean, secondaValue: boolean): boolean {
    return firstValue !== secondaValue;
  }

  validateOrValueList(list: boolean[]): boolean {
    return list.some((value) => value);
  }
  validateLimitPayMethod(methodType: string, total: number): boolean {
    const dictGetLimitPaymentMethods: { [key: number]: number } = {
      0: Number(this.findParamsValue(CatalogFields.oxxoLimit)) ?? 0,
      1: Number(this.findParamsValue(CatalogFields.cardLimit)) ?? 0,
    };

    const vaidateValuesOXXO: ValidateSameValues[] = [
      {
        firstValue: methodType === PaymentTypeInfo.oxxo,
        secondValue: true,
      },
      {
        firstValue: total > dictGetLimitPaymentMethods[Number(false)],
        secondValue: true,
      },
    ];
    const vaidateValuesCard: ValidateSameValues[] = [
      {
        firstValue: methodType === PaymentTypeInfo.card,
        secondValue: true,
      },
      {
        firstValue: total > dictGetLimitPaymentMethods[Number(true)],
        secondValue: true,
      },
    ];
    if (
      this.validateOrValue(
        this.validateSameValues(vaidateValuesOXXO),
        this.validateSameValues(vaidateValuesCard)
      )
    ) {
      this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
        typeIcon: TypeIcon.info,
        textToShow: `${Messages.paymentMethodValidation
          }${dictGetLimitPaymentMethods[
            Number(methodType === PaymentTypeInfo.card)
          ].toLocaleString('es-MX')} MXN, intenta con otro método.`,
        isButtonCancel: false,
        typeMessage: MessageType.none,
      });
      return true;
    }
    return false;
  }

  roundNumber2Dicemals(value: number): number {
    let decimalPart = value - Math.floor(value);
    decimalPart = Number(decimalPart.toFixed(5));
    if (decimalPart > 0) {
      decimalPart *= 100;
      decimalPart = Math.ceil(decimalPart);
      decimalPart /= 100;
      return Math.floor(value) + decimalPart;
    }
    return value;
  }
  validateCartContentType(list: IProducts[]): CartContentType {
    let contentType = this.calculateTernary(
      list.every((product) => product.isPackage),
      CartContentType.onlyPackages,
      CartContentType.packagesAndProducts
    );
    contentType = this.calculateTernary(
      list.every((product) => !product.isPackage),
      CartContentType.onlyProducts,
      contentType
    );
    return contentType;
  }

  calculateTernary = (
    validation: boolean,
    firstValue: any,
    secondaValue: any
  ): any => (validation ? firstValue : secondaValue)

  setbBrandPayment(paymentInformation: PaymentInformation): string {
    const typeMethod = this.calculateTernary(
      paymentInformation.object === PaymentTypeInfoCompare.card,
      paymentInformation.brand,
      paymentInformation.object
    );
    return this.getIconOfPaymentMethod(typeMethod);
  }

  filterBySelectedParentElements(
    selectedParentList: ItemClassification[],
    childNoFilterList: ItemClassification[],
    isAilmentColumn: boolean
  ): ItemClassification[] {
    let filterChildList: ItemClassification[] = [];
    if (selectedParentList.length === 0) {
      filterChildList = Object.assign([], childNoFilterList);
      return filterChildList;
    }
    childNoFilterList.forEach((noFilterChildItem) => {
      let isOnAllSelecteds = true;
      if (isAilmentColumn) {
        isOnAllSelecteds = this.validateExistsAllIdsInParentList(
          selectedParentList,
          noFilterChildItem.ailmentIds ?? []
        );
      } else {
        isOnAllSelecteds = this.validateExistsAllIdsInParentList(
          selectedParentList,
          noFilterChildItem.specialityIds ?? []
        );
      }
      if (isOnAllSelecteds) {
        filterChildList.push(noFilterChildItem);
      }
    });
    return filterChildList;
  }

  validateExistsAllIdsInParentList(
    parentList: ItemClassification[],
    childParentsids: number[]
  ): boolean {
    let existsInAll = true;
    parentList.forEach((parentElement) => {
      if (!childParentsids.includes(parentElement.id)) {
        existsInAll = false;
      }
    });
    return existsInAll;
  }

  getListOfItemsInAvailable(
    availableItems: ItemClassification[],
    selectedItems: ItemClassification[]
  ): ItemClassification[] {
    return availableItems.filter((active) => {
      return selectedItems.includes(active);
    });
  }

  filterIfExistsInSomeParentElement(
    selectedParentList: ItemClassification[],
    childNoFilterList: ItemClassification[]
  ): ItemClassification[] {
    let filterChildList: ItemClassification[] = [];
    if (selectedParentList.length === 0) {
      filterChildList = Object.assign([], childNoFilterList);
      return filterChildList;
    }
    childNoFilterList.forEach((childNoFilterItem) => {
      const parentsId = selectedParentList.map(
        (selectedItem) => selectedItem.id
      );
      childNoFilterItem.specialityIds.forEach((childFilterSpecialtyId) => {
        if (parentsId.includes(childFilterSpecialtyId)) {
          filterChildList.push(childNoFilterItem);
        }
      });
    });
    const noDuplicates = new Set(filterChildList);
    const result = [...noDuplicates];
    return result;
  }

  assignObjectToIproduct(detailProducts: DetailProduct[], defaultQuantity: boolean): IProducts[] {
    const resultList: IProducts[] = [];
    detailProducts.forEach((detailProduct) => {
      const product = new IProducts();
      product.additionalText = detailProduct.additionalText;
      product.taxRate = detailProduct.taxRate;
      product.description = detailProduct.description;
      product.costPerPiece = detailProduct.costPerPiece;
      product.costPerPieceDiscount = detailProduct.costPerPieceDiscount;
      product.isLine = detailProduct.isLine;
      product.itemCode = detailProduct.itemCode;
      product.itemCodeHasMissing = detailProduct.itemCodeHasMissing;
      product.arriveDateProduct = detailProduct.arriveDateProduct;
      product.displayUnique = detailProduct.displayUnique;
      product.imageUrl = detailProduct.imgProduct;
      product.imgProduct = detailProduct.imgProduct;

      const attribute = new ProductAttributes();
      attribute.quantity = this.calculateTernary(defaultQuantity, 1, detailProduct.quantity);
      attribute.container = detailProduct.container;
      attribute.containerCost = detailProduct.containerCost;
      attribute.containerCostDiscount = detailProduct.containerCostDiscount;
      attribute.isFavorite = detailProduct.isFavorite;
      attribute.label = detailProduct.label;
      attribute.containerTaxRate = detailProduct.containerTaxRate;
      attribute.containerHasMissing = detailProduct.containerHasMissing;
      attribute.arriveDateContainer = detailProduct.arriveDateContainer;
      attribute.containerCatalogGroup = detailProduct.catalogGroupContainer ?? StringConst.empty;
      attribute.containerItemCode = detailProduct.containerItemCode;
      attribute.invalidLabels = detailProduct.invalidLabels;
      attribute.priceWithDiscount = detailProduct.priceWithDiscount;
      attribute.costPerPiece = detailProduct.costPerPiece;
      attribute.costPerPieceDiscount = detailProduct.costPerPieceDiscount;

      product.productAttributes = [attribute];
      product.catalogGroup = detailProduct.catalogGroup;
      product.missingPrinciplesActives = detailProduct.missingPrinciplesActives;
      product.hoffmanText = detailProduct.hoffmanText;
      product.productFirmName = detailProduct.productFirmName;
      product.priceWithDiscount = detailProduct.priceWithDiscount;
      product.invalidLabels = detailProduct.invalidLabels;
      resultList.push(product);
    });
    return resultList;
  }

  getTypeReloadShoppingCar(index: number, typeCart: number): ReloadShoppingCar {
    const dictType: { [key: number]: TypeShoppingCar } = {
      1: TypeShoppingCar.shoppingCar,
      5: TypeShoppingCar.saveShoppingCar
    };
    const shopping = new ReloadShoppingCar();
    shopping.typeCart = dictType[typeCart] ?? TypeShoppingCar.shoppingCar;
    shopping.index = index;
    return shopping;
  }

  normalizeString(data: string): string {
    return data.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  validateProductsListHasValidPieces(productList: IProducts[]): boolean {
    const attributes = productList.map((product) => product.productAttributes)
      .reduce((e1, e2) => e1.concat(e2), []);
    return attributes.every((attribute) => attribute.quantity > 0);
  }

  capitalizeString(text: string): string {
    const lower = text.toLocaleLowerCase();
    return `${text.charAt(0).toUpperCase()}${lower.slice(1)}`;
  }

  capitalizeName(name: string): string {
    return name.split('')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
  }

  hideHomeItem(homeOption: ItemHome[], itemName: string, rol: string): ItemHome[] {
    const currentRol = this.localStorageService.getUserInRole();
    if (currentRol === rol) {
      homeOption.forEach((element, index) => {
        if (element.name === itemName) {
          homeOption.splice(index, 1);
        }
      });
    }
    return homeOption;
  }

  getShortTransactionId(completeTransaction: string): string {
    return completeTransaction.slice(-6).toUpperCase();
  }

  validateValidRecipeDirection(direction: DirectionsAndTaxs): boolean {
    const stringValues: string[] = [direction.city, direction.suburb, direction.phoneNumber,
    direction.state, direction.street, direction.streetNumber, direction.zipCode];
    return this.validateAndValue(stringValues.map(value => this.validateValidString(value)));
  }

  convertDirection(shoppingCartDirection: PrescritionInfoHeader): DirectionsAndTaxs {
    const data = new DirectionsAndTaxs();
    data.phoneNumber = shoppingCartDirection.cellPhone;
    data.zipCode = shoppingCartDirection.zipCode;
    data.streetNumber = shoppingCartDirection.streetNumber;
    data.street = shoppingCartDirection.street;
    data.state = shoppingCartDirection.state;
    data.suburb = shoppingCartDirection.neighborhood;
    data.city = shoppingCartDirection.municipality;
    return data;
  }

  validateRecipeConfigHasCustomNames(recipes: PatientsConfigByReRecipe[]): boolean {
    const customList = recipes.map(recipe => recipe.patients.map(patient => patient.isNamePrinted)).reduce((a1, a2) => a1.concat(a2), []);
    return this.validateOrValueList(customList);
  }

  twoCeros = (numero: number) => (numero < 10 ? `0${numero}` : numero);

  getDateFormat = (date: Date): string => {
    return date.toISOString().slice(0, 10);
  }

  getPaymentMethodIcon(method: string, brand: string, bbvaSubmethod: string): string {
    if (!method) {
      return '';
    }
    switch (method) {
      case PaymentTypeInfo.card:
        return this.getIconOfPaymentMethod(brand);
      case PaymentTypeInfo.bbvaMulti:
        return this.getIconOfPaymentMethod(bbvaSubmethod ?? PaymentTypeInfo.bbvaMulti);
      default:
        return this.getIconOfPaymentMethod(method);
    }
  }

  getDoctorSpecialty(rol: string): string {
    return this.calculateTernary(
      rol === RolesString.doctor,
      this.localStorageService.getHeaderInfo().groupName,
      this.localStorageService.getClientDoctor().speciality
    );
  }

  findParamsValue(key: CatalogFields): string {
    return this.localStorageService.getDinamicParams().find(item => item.field === key)?.value ?? StringConst.empty;
  }
  getProductsToUpdate = (
    maxPieces: ProductMaxPieces[],
    minPieces: ProductMinPieces[]
  ): UpdateProduct[] => {
    const itemsMaxPieces = maxPieces.map((item) => item.itemCode);
    const itemsMinPieces = minPieces
      .map((item) => item.itemCode)
      .filter((itemCode) => !itemsMaxPieces.includes(itemCode));
    const itemCodes = [...itemsMaxPieces, ...itemsMinPieces];
    return this.convert(itemCodes);
  }
  convert = (items: string[]): UpdateProduct[] => {
    return items.map((item) => {
      return {
        userId: StringConst.empty,
        itemCode: item,
        quantity: NumberCons.zero,
        container: StringConst.empty,
        label: StringConst.empty,
        needsPersitance: false,
        cartType: NumberCons.zero,
        needSumQuantity: false,
        needsFavorite: false,
        isLine: StringConst.empty,
        isPackage: false,
        needContainerValidation: false,
        containerToReplace: StringConst.empty,
        priceListId: this.localStorageService.getPriceList()
      };
    });
  }

  setHasChanges(hasChanges: boolean): void {
    this.hasChanges = hasChanges;
  }

  getHasChanges(): boolean {
    return this.hasChanges;
  }

  getInitialFilter(field: string): SAPFilterRequest {
    const actualDate = new Date();
    const firstDayOfMonth = new Date(actualDate.getFullYear(), actualDate.getMonth(), 1);
    const lastDayOfMonth = new Date(actualDate.getFullYear(), actualDate.getMonth() + 1, 0);
    const item = new SAPFilterRequest();
    item.field = field;
    item.startValue = moment(firstDayOfMonth).format('YYYY-MM-DD').toString();
    item.finishValue = moment(lastDayOfMonth).format('YYYY-MM-DD').toString();
    item.type = TypeReportsFilter.range;
    item.valueType = ValueTypesFilter.date;
    return item;
  }

  getRangeChange(data: SelectedDateRange): SAPFilterRequest {
    const request = new SAPFilterRequest();
    const endDate = new Date(data.range.end.getFullYear(), data.range.end.getMonth() + 1, 0);
    request.startValue = moment(data.range.start).format('YYYY-MM-DD').toString();
    request.finishValue = moment(endDate).format('YYYY-MM-DD').toString();
    return request;
  }

  calculatePriceWithDiscount(value: number, percentageDiscount: number): number {
    return value * ((100 - percentageDiscount) / 100);
  }

  searchStringInStringArray(stringToSearch: string, array: string[]): boolean {
    return array.includes(stringToSearch);
  }

  encodePercent(str: string): string {
    return str.replace(/%/g, '%25');
  }
}
